import { AdvancedSelect } from '../../../../widgets/select';
import { BasicButton, PrimaryButton } from '../../../../widgets/buttons';
import { Dialog } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { T, toLocalID } from 'src/shared/helpers';
import { assetLibraryQueriesAIAssetLibraryCategoriesQuery } from '../../assetLibrary/__generated__/assetLibraryQueriesAIAssetLibraryCategoriesQuery.graphql';
import { aiAssetLibraryCategoriesQuery } from '../../assetLibrary/assetLibraryQueries';
import { DialogContent, DialogFooter, DialogLoadingState } from '../shared/sharedStyles';

interface MoveToCategoryDialogProps {
    currentCategoryId: number | string | null;
    onMove: (categoryId: number | null) => void;
    onClose: () => void;
}

const MoveToCategoryDialogContent: FC<MoveToCategoryDialogProps> = ({ currentCategoryId, onMove, onClose }) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
        currentCategoryId ? (typeof currentCategoryId === 'number' ? currentCategoryId.toString() : currentCategoryId) : '-1'
    );

    const categoriesData = useLazyLoadQuery<assetLibraryQueriesAIAssetLibraryCategoriesQuery>(
        aiAssetLibraryCategoriesQuery,
        {},
        { fetchPolicy: 'store-or-network' }
    );

    const categories = categoriesData.aiAssetLibraryCategories?.nodes || [];

    const categoryOptions: { value: string; label: string }[] = useMemo(
        () => [{ value: '-1', label: T._('No Category', 'Admin') }, ...categories.map((cat) => ({ value: cat.id, label: cat.name }))],
        [categories]
    );

    const selectedCategoryOption = useMemo(() => categoryOptions.find((opt) => opt.value === selectedCategoryId), [categoryOptions, selectedCategoryId]);

    const handleMove = useCallback(() => {
        const categoryId = selectedCategoryId === '-1' ? null : toLocalID(selectedCategoryId);
        onMove(categoryId);
        onClose();
    }, [selectedCategoryId, onMove, onClose]);

    return (
        <DialogContent>
            <FormControl label={T._('Select Category', 'Admin')}>
                <AdvancedSelect
                    value={selectedCategoryOption}
                    onChange={(opt: any) => setSelectedCategoryId(opt?.value || '-1')}
                    options={categoryOptions}
                    placeholder={T._('Select a category...', 'Admin')}
                    autoFocus
                    menuPosition='fixed'
                />
            </FormControl>

            <DialogFooter>
                <BasicButton onClick={onClose}>{T._('Cancel', 'Admin')}</BasicButton>
                <PrimaryButton onClick={handleMove}>{T._('Move', 'Admin')}</PrimaryButton>
            </DialogFooter>
        </DialogContent>
    );
};

export const MoveToCategoryDialog: FC<MoveToCategoryDialogProps> = (props) => {
    return (
        <Dialog title={T._('Move to Category', 'Admin')} onClose={props.onClose}>
            <Suspense fallback={<DialogLoadingState>{T._('Loading...', 'Admin')}</DialogLoadingState>}>
                <MoveToCategoryDialogContent {...props} />
            </Suspense>
        </Dialog>
    );
};
